/**
 * authEnterpriseHandlers
 *
 * Enterprise user verification and handling utilities.
 * Extracted from AuthContext for modularity.
 */
import { SERVER_URL } from '../../api/core';
import api from '../../api';

/**
 * Check if user is an enterprise user
 * ONLY by database flag, NOT by email domain
 * 
 * @param {Object} user - User object from context
 * @returns {boolean}
 */
export const isEnterpriseUser = (user) => {
  return user?.isEnterpriseUser === true || user?.isEnterprise === true;
};

/**
 * Check if enterprise user needs email verification
 * 
 * @param {Object} user - User object from context
 * @param {Object} firebaseUser - Firebase user object
 * @returns {boolean}
 */
export const enterpriseNeedsVerification = (user, firebaseUser) => {
  const isEnterprise = isEnterpriseUser(user);
  const emailVerified = firebaseUser?.emailVerified ?? false;
  
  return isEnterprise && !emailVerified;
};

/**
 * Fetch enterprise data for a given enterprise ID
 * 
 * @param {string} enterpriseId - Enterprise ID
 * @returns {Promise<Object|null>} - Enterprise data or null
 */
export const fetchEnterpriseData = async (enterpriseId) => {
  if (!enterpriseId) return null;
  
  try {
    const enterpriseResponse = await api.get(`/admin/enterprises/${enterpriseId}`);
    return enterpriseResponse.data?.enterprise || null;
  } catch (err) {
    console.error('authEnterpriseHandlers: Error fetching enterprise data:', err);
    return null;
  }
};

/**
 * Check if email domain belongs to an enterprise
 * 
 * @param {string} email - User email
 * @returns {Promise<Object|null>} - Enterprise data if found, null otherwise
 */
export const checkEnterpriseDomain = async (email) => {
  if (!email) return null;
  
  const emailDomain = email.split('@')[1]?.toLowerCase();
  if (!emailDomain) return null;
  
  try {
    const response = await fetch(`${SERVER_URL}/enterprise/check-domain/${emailDomain}`);
    const data = await response.json();
    
    if (data.exists && data.enterprise) {
      return data.enterprise;
    }
  } catch (error) {
    console.error('authEnterpriseHandlers: Error checking enterprise domain:', error);
  }
  
  return null;
};

/**
 * Create minimal user object for unverified enterprise users
 * Used when profile doesn't exist yet but user is from enterprise domain
 * 
 * @param {Object} firebaseUser - Firebase user object
 * @param {Object} enterprise - Enterprise data
 * @returns {Object} - Minimal user object
 */
export const createMinimalEnterpriseUser = (firebaseUser, enterprise) => {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    firstName: firebaseUser.displayName?.split(' ')[0] || 'User',
    lastName: firebaseUser.displayName?.split(' ')[1] || '',
    planLevel: 'basic',
    isEnterpriseUser: true,
    requiresEmailVerification: true,
    enterpriseVerified: false,
    enterprise: enterprise
  };
};

/**
 * Build enterprise navigation state for verification redirect
 * 
 * @param {Object} enterprise - Enterprise data
 * @param {Object} user - User object
 * @returns {Object} - Navigation state object
 */
export const buildEnterpriseVerificationState = (enterprise, user) => {
  return {
    enterprise: enterprise,
    isEnterprise: true,
    userEmail: user?.email,
    userId: user?.uid
  };
};

/**
 * Get redirect destination for enterprise user
 * Enterprise users never go to pricing page
 * 
 * @param {Object} user - User object
 * @param {boolean} isPremiumOrPro - Whether user has premium/pro plan
 * @returns {string} - Redirect path
 */
export const getEnterpriseRedirectDestination = (user, isPremiumOrPro) => {
  // Enterprise users ALWAYS go to app, never to pricing
  if (isEnterpriseUser(user)) {
    return '/app/';
  }
  
  // Non-enterprise logic
  if (isPremiumOrPro) {
    return '/app/';
  }
  
  return '/pricing';
};

